import { html, css, CSSResultGroup, TemplateResult } from 'lit'
import { customElement, property, query, queryAssignedElements, state } from 'lit/decorators.js'
import { BaseComponent } from './base-component.js'

@customElement('il-dropdown')
export default class DropdownComponent extends BaseComponent {
  @property({ type: String }) placeholder: string = ''
  @property({ type: String }) label: string = ''
  @property({ type: String }) detail: string = ''
  @property({ type: String }) value: string = ''
  @property({ type: String }) align: 'left' | 'right' = 'left'
  @property({ type: Boolean }) truncateLabel: boolean = false
  @property({ type: Object }) onChange?: (value: string) => void

  @queryAssignedElements({ slot: 'label', flatten: true })
  private _labelElements!: Element[]

  @queryAssignedElements({ slot: 'label-after', flatten: true })
  private _labelAfterElements!: Element[]

  @queryAssignedElements({ slot: 'footer', flatten: true })
  private _footerElements!: Element[]

  @query('il-dropdown-header')
  private _headerEl!: HTMLElement

  @query('.il-dropdown__list')
  private _listEl!: HTMLElement

  // UI state cal vars
  @state() private _hasLabel: boolean = false
  @state() private _panelTop: number = 0
  @state() private _panelLeft: number = 0
  @state() private _panelRight: number = 0
  @state() private _panelWidth: number = 0
  @state() private _panelBottom: number = 0
  @state() private _openUpward: boolean = false
  @state() private _hasLabelAfter: boolean = false
  @state() private _hasFooter: boolean = false
  @state() private _open: boolean = false
  @state() private _selectedLabel: string = ''
  @state() private _selectedDetailText: string = ''

  override connectedCallback(): void {
    super.connectedCallback()

    this.addEventListener('keydown', this._onHostKeydown)
    document.addEventListener('click', this._onDocumentClick)
    window.addEventListener('scroll', this._onScrollOrResize, true)
    window.addEventListener('resize', this._onScrollOrResize)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()

    this.removeEventListener('keydown', this._onHostKeydown)
    document.removeEventListener('click', this._onDocumentClick)
    window.removeEventListener('scroll', this._onScrollOrResize, true)
    window.removeEventListener('resize', this._onScrollOrResize)
  }

  override updated(changedProps: Map<string, unknown>): void {
    if (changedProps.has('_open') && this._open) {
      this._updatePanelPosition()
      this._scrollToSelected()
    }
  }

  private _onDocumentClick = (e: MouseEvent): void => {
    if (!this._open) return

    if (!e.composedPath().includes(this)) {
      this._open = false
    }
  }

  private _onHostKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._open = false
    }
  }

  private _onScrollOrResize = (): void => {
    if (this._open) this._updatePanelPosition()
  }

  // useful for when opening the dropdown and selected option is one of the items on the bottom of the list
  // this logic will try to scroll to the starting position of the selected option
  private _updatePanelPosition(): void {
    const rect = this._headerEl?.getBoundingClientRect()
    if (!rect) return

    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const PANEL_MAX_HEIGHT = 410

    this._openUpward = spaceBelow < PANEL_MAX_HEIGHT && spaceAbove > spaceBelow
    this._panelLeft = rect.left
    // clientWidth excludes the scrollbar, matching what position: fixed measures right from
    this._panelRight = document.documentElement.clientWidth - rect.right
    this._panelWidth = rect.width

    if (this._openUpward) {
      this._panelBottom = window.innerHeight - rect.top
    } else {
      this._panelTop = rect.bottom
    }
  }

  private _scrollToSelected(): void {
    const listEl = this._listEl
    if (!listEl) return

    const selectedEl = this.querySelector('il-dropdown-option[selected]') as HTMLElement | null
    if (!selectedEl) return

    const listRect = listEl.getBoundingClientRect()
    const optRect = selectedEl.getBoundingClientRect()
    const offset = optRect.top - listRect.top + listEl.scrollTop
    listEl.scrollTop = offset
  }

  // selecting an option is not as simple as setting a value, we also need to update the UI state
  private _onOptionSelect = (e: Event): void => {
    const { value, label, detailText } = (e as CustomEvent<{ value: string; label: string; detailText: string }>).detail

    this.value = value
    this._open = false
    this._selectedLabel = label
    this._selectedDetailText = detailText

    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value },
        bubbles: true,
        composed: true,
      })
    )
    this.onChange?.(value)
  }

  private _onLabelSlotChange(): void {
    this._hasLabel = Boolean(this._labelElements?.length)
  }

  private _onLabelAfterSlotChange(): void {
    this._hasLabelAfter = Boolean(this._labelAfterElements?.length)
  }

  private _onFooterSlotChange(): void {
    this._hasFooter = Boolean(this._footerElements?.length)
  }

  private _renderLabel(): TemplateResult {
    if (this._hasLabel || this._hasLabelAfter) {
      return html`
        <div class="il-dropdown__label-wrapper">
          <div class="il-dropdown__label">
            <slot name="label" @slotchange="${this._onLabelSlotChange}"></slot>
          </div>

          ${this._hasLabelAfter
            ? html`<slot name="label-after" @slotchange="${this._onLabelAfterSlotChange}"></slot>`
            : ''}
        </div>
      `
    }

    return html`
      <slot name="label" @slotchange="${this._onLabelSlotChange}"></slot>
      <slot name="label-after" @slotchange="${this._onLabelAfterSlotChange}"></slot>
    `
  }

  override render(): TemplateResult {
    return html`
      <div class="${this.baseCSSClass} il-dropdown" data-test-iliad-dropdown>
        ${this._renderLabel()}

        <il-dropdown-header
          label="${this._selectedLabel || this.label || this.placeholder}"
          detail="${this._selectedDetailText || this.detail}"
          .expanded="${this._open}"
          .truncateLabel="${this.truncateLabel}"
          .onClick="${() => {
            this._open = !this._open
          }}"
        ></il-dropdown-header>

        <div
          part="panel"
          class="
            il-dropdown__panel
            ${this._open ? 'il-dropdown__panel--open' : ''}
            ${this._openUpward ? 'il-dropdown__panel--up' : ''}
          "
          style="
            ${this._openUpward ? `bottom: ${this._panelBottom}px;` : `top: ${this._panelTop}px;`}
            ${this.align === 'right' ? `right: ${this._panelRight}px;` : `left: ${this._panelLeft}px;`}
            width: ${this._panelWidth}px;
          "
        >
          <div class="il-dropdown__list">
            <slot @option-select="${this._onOptionSelect}"></slot>
          </div>

          <div class="il-dropdown__footer ${this._hasFooter ? 'il-dropdown__footer--visible' : ''}">
            <slot name="footer" @slotchange="${this._onFooterSlotChange}"></slot>
          </div>
        </div>
      </div>
    `
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      :host {
        display: block;
      }

      .il-dropdown__label-wrapper {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;
      }

      .il-dropdown__label {
        flex-shrink: 0;
      }

      .il-dropdown__panel {
        display: none;
        position: fixed;
        z-index: 9999;
        background-color: var(--il-color-neutrals-0);
        border: 1px solid var(--il-color-neutrals-100);
        border-top: none;
        border-radius: 0 0 6px 6px;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
        overflow: hidden;
      }

      .il-dropdown__panel--up {
        border-top: 1px solid var(--il-color-neutrals-100);
        border-bottom: none;
        border-radius: 6px 6px 0 0;
        box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.08);
      }

      .il-dropdown__panel.il-dropdown__panel--open {
        display: block;
      }

      .il-dropdown__list {
        padding: 8px 0;
        max-height: 350px;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--il-color-neutrals-200) transparent;
      }

      .il-dropdown__list::-webkit-scrollbar {
        width: 2px;
      }

      .il-dropdown__list::-webkit-scrollbar-track {
        background: transparent;
      }

      .il-dropdown__list::-webkit-scrollbar-thumb {
        background-color: var(--il-color-neutrals-200);
      }

      .il-dropdown__footer {
        display: none;
      }

      .il-dropdown__footer--visible {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        border-top: 1px solid var(--il-color-neutrals-100);
        gap: 8px;
      }
    `,
  ] as CSSResultGroup
}

declare global {
  interface HTMLElementTagNameMap {
    'il-dropdown': DropdownComponent
  }
}
